import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, TableWrap, Badge, Tabs, Btn } from "../shared/components"
import {
  listRecipients,
  sendEmailToUsers,
  sendBroadcastEmail,
  listEmailLogs,
} from "../api/emails.api"
import { listCourses } from "../api/courses.api"

const TABS = ["compose", "logs"]

function fmtDate(d, lang) {
  if (!d) return "—"
  return new Date(d).toLocaleString(lang === "id" ? "id-ID" : "en-US", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })
}

function StatusTag({ status }) {
  if (status === "sent") return <Badge tone="success">sent</Badge>
  if (status === "mocked") return <Badge tone="brand">mocked</Badge>
  if (status === "failed") return <Badge tone="danger">failed</Badge>
  return <Badge>{status}</Badge>
}

export default function EmailsScreen() {
  const t = useT()
  const { lang, currentProgramId, currentProgram } = useApp()
  const orgName = useOrgName()
  const [tab, setTab] = React.useState("compose")

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.code?.toUpperCase() || orgName} · {lang === "id" ? "Komunikasi" : "Communications"}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {lang === "id" ? "Email Peserta" : "Participant Emails"}
          </h1>
          <div className="muted mt-1">
            {lang === "id"
              ? `Kirim email ke peserta ${currentProgram?.name || "program ini"}.`
              : `Send emails to participants of ${currentProgram?.name || "this program"}.`}
          </div>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: "compose", label: "Compose" },
          { id: "logs", label: lang === "id" ? "Riwayat" : "History" },
        ]}
        value={tab}
        onChange={setTab}
      />

      <div style={{ marginTop: 16 }}>
        {tab === "compose" ? <ComposeTab /> : <LogsTab />}
      </div>
    </div>
  )
}

function ComposeTab() {
  const { lang, currentProgramId } = useApp()
  const isMobile = useIsMobile()
  const [mode, setMode] = React.useState("broadcast") // broadcast | individual
  const [recipients, setRecipients] = React.useState([])
  const [selectedIds, setSelectedIds] = React.useState(new Set())
  const [courses, setCourses] = React.useState([])
  const [filterCourseId, setFilterCourseId] = React.useState("")
  const [filterCertEligible, setFilterCertEligible] = React.useState(false)
  const [subject, setSubject] = React.useState("")
  const [body, setBody] = React.useState("")
  const [busy, setBusy] = React.useState(false)

  const reloadRecipients = React.useCallback(() => {
    if (!currentProgramId) { setRecipients([]); return }
    listRecipients(currentProgramId, {
      courseId: filterCourseId || undefined,
      certEligible: filterCertEligible,
    })
      .then(setRecipients)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
  }, [currentProgramId, filterCourseId, filterCertEligible])

  React.useEffect(() => { reloadRecipients() }, [reloadRecipients])

  React.useEffect(() => {
    if (!currentProgramId) return
    listCourses(currentProgramId).then(setCourses).catch(() => setCourses([]))
  }, [currentProgramId])

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedIds(new Set(recipients.map((r) => r.id)))
  const clearAll = () => setSelectedIds(new Set())

  const handleSend = async (e) => {
    e.preventDefault()
    if (!subject || !body) {
      toast.error(lang === "id" ? "Subject dan isi wajib diisi" : "Subject and body required")
      return
    }

    setBusy(true)
    try {
      let result
      if (mode === "individual") {
        if (selectedIds.size === 0) {
          toast.error(lang === "id" ? "Pilih minimal 1 peserta" : "Select at least 1 recipient")
          return
        }
        result = await sendEmailToUsers(currentProgramId, {
          recipientUserIds: Array.from(selectedIds),
          subject,
          body,
        })
      } else {
        result = await sendBroadcastEmail(currentProgramId, {
          subject,
          body,
          filter: {
            courseId: filterCourseId || undefined,
            certEligible: filterCertEligible,
          },
        })
      }
      toast.success(
        lang === "id"
          ? `Terkirim: ${result.sent} · Gagal: ${result.failed}`
          : `Sent: ${result.sent} · Failed: ${result.failed}`
      )
      setSubject(""); setBody(""); setSelectedIds(new Set())
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  const targetCount = mode === "individual" ? selectedIds.size : recipients.length

  return (
    <div className="grid mt-4" style={{gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: 16}}>
      <form onSubmit={handleSend} className="col gap-3">
        <div className="tile">
          <div className="tile-header"><h3>{lang === "id" ? "Mode" : "Mode"}</h3></div>
          <div className="col gap-2" style={{padding: 16}}>
            <label className="row gap-2">
              <input type="radio" checked={mode === "broadcast"} onChange={() => setMode("broadcast")} />
              <span>{lang === "id" ? "Broadcast (semua peserta + filter)" : "Broadcast (all participants + filter)"}</span>
            </label>
            <label className="row gap-2">
              <input type="radio" checked={mode === "individual"} onChange={() => setMode("individual")} />
              <span>{lang === "id" ? "Pilih peserta tertentu" : "Select specific recipients"}</span>
            </label>
          </div>
        </div>

        <div className="tile">
          <div className="tile-header"><h3>{lang === "id" ? "Isi Email" : "Email Content"}</h3></div>
          <div className="col gap-3" style={{padding: 16}}>
            <label className="col gap-1">
              <span className="small">{lang === "id" ? "Subject" : "Subject"}</span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder={lang === "id" ? "Pengingat jadwal drill besok" : "Reminder: drill tomorrow"}
                style={{height: 32, padding: "0 8px", border: "1px solid var(--border-strong)"}}
              />
            </label>
            <label className="col gap-1">
              <span className="small">{lang === "id" ? "Isi pesan" : "Body"}</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                rows={10}
                placeholder={lang === "id" ? "Halo, ini pengingat..." : "Hi, this is a reminder..."}
                style={{padding: 8, border: "1px solid var(--border-strong)", fontFamily: "inherit", fontSize: 14, lineHeight: 1.5}}
              />
              <span className="tiny muted">{body.length} / 10000</span>
            </label>
          </div>
          <div className="tile-foot">
            <span className="small">
              {lang === "id" ? "Target: " : "Target: "}<strong>{targetCount}</strong> {lang === "id" ? "peserta" : "recipients"}
            </span>
            <button type="submit" className="btn sm" disabled={busy || targetCount === 0 || !subject || !body}>
              <Icon name="send" size={12}/>
              {busy
                ? (lang === "id" ? "Mengirim..." : "Sending...")
                : (lang === "id" ? "Kirim Email" : "Send Email")}
            </button>
          </div>
        </div>
      </form>

      <div className="col gap-3">
        <div className="tile">
          <div className="tile-header">
            <h3>{lang === "id" ? "Filter" : "Filter"}</h3>
            <span className="tile-header-sub">{recipients.length} {lang === "id" ? "ditemukan" : "matched"}</span>
          </div>
          <div className="col gap-3" style={{padding: 16}}>
            <label className="col gap-1">
              <span className="small">{lang === "id" ? "Kursus" : "Course"}</span>
              <select
                value={filterCourseId}
                onChange={(e) => setFilterCourseId(e.target.value)}
                style={{height: 32, padding: "0 8px", border: "1px solid var(--border-strong)"}}
              >
                <option value="">{lang === "id" ? "Semua kursus" : "All courses"}</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </label>

            <label className="row gap-2">
              <input
                type="checkbox"
                checked={filterCertEligible}
                onChange={(e) => setFilterCertEligible(e.target.checked)}
              />
              <span className="small">{lang === "id" ? "Hanya yang punya sertifikat" : "Only cert holders"}</span>
            </label>
          </div>
        </div>

        {mode === "individual" && (
          <div className="tile">
            <div className="tile-header">
              <h3>{lang === "id" ? "Pilih Peserta" : "Select Recipients"}</h3>
              <span className="tile-header-sub">{selectedIds.size} / {recipients.length}</span>
            </div>
            <div className="row gap-2" style={{padding: "8px 16px"}}>
              <Btn variant="ghost" size="sm" onClick={selectAll}>
                {lang === "id" ? "Pilih semua" : "Select all"}
              </Btn>
              <Btn variant="ghost" size="sm" onClick={clearAll}>
                {lang === "id" ? "Bersihkan" : "Clear"}
              </Btn>
            </div>
            <div style={{maxHeight: 280, overflowY: "auto"}}>
              {recipients.length === 0 ? (
                <div className="muted small" style={{padding: 16, textAlign: "center"}}>
                  {lang === "id" ? "Tidak ada peserta." : "No recipients."}
                </div>
              ) : recipients.map((r) => (
                <label
                  key={r.id}
                  className="row gap-2"
                  style={{padding: "8px 16px", borderTop: "1px solid var(--border)", cursor: "pointer"}}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleSelected(r.id)}
                  />
                  <div className="col" style={{flex: 1, minWidth: 0}}>
                    <span className="small text-truncate">{r.full_name}</span>
                    <span className="tiny muted text-truncate">{r.email}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function LogsTab() {
  const { lang, currentProgramId } = useApp()
  const [logs, setLogs] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!currentProgramId) return
    setLoading(true)
    listEmailLogs(currentProgramId, 200)
      .then(setLogs)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId])

  const total = logs.length
  const sent = logs.filter((l) => l.status === "sent" || l.status === "mocked").length
  const failed = logs.filter((l) => l.status === "failed").length

  return (
    <div className="col gap-4 mt-4">
      <div className="grid grid-4">
        <Stat label={lang === "id" ? "Total" : "Total"} value={String(total)} />
        <Stat label={lang === "id" ? "Terkirim" : "Sent"} value={String(sent)} />
        <Stat label={lang === "id" ? "Gagal" : "Failed"} value={String(failed)} />
        <Stat label={lang === "id" ? "Jenis" : "Kinds"} value={String(new Set(logs.map((l) => l.kind)).size)} />
      </div>

      <div className="tile">
        <div className="tile-header">
          <h3>{lang === "id" ? "Riwayat Email" : "Email History"}</h3>
          <span className="tile-header-sub">{loading ? (lang === "id" ? "Memuat..." : "Loading...") : `${logs.length} ${lang === "id" ? "entri" : "entries"}`}</span>
        </div>
        {logs.length === 0 && !loading ? (
          <div className="tile-pad muted small" style={{textAlign: "center"}}>
            {lang === "id" ? "Belum ada email terkirim." : "No emails sent yet."}
          </div>
        ) : (
          <TableWrap>
          <table className="table">
            <thead><tr>
              <th>{lang === "id" ? "Waktu" : "Time"}</th>
              <th>{lang === "id" ? "Penerima" : "Recipient"}</th>
              <th>{lang === "id" ? "Subject" : "Subject"}</th>
              <th>{lang === "id" ? "Jenis" : "Kind"}</th>
              <th>{lang === "id" ? "Status" : "Status"}</th>
            </tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="mono tiny">{fmtDate(l.created_at, lang)}</td>
                  <td>
                    <div className="small" style={{fontWeight: 500}}>{l.to_name || "—"}</div>
                    <div className="tiny muted">{l.to_email}</div>
                  </td>
                  <td className="small">{l.subject}</td>
                  <td><span className="tag outline tiny">{l.kind}</span></td>
                  <td><StatusTag status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableWrap>
        )}
      </div>
    </div>
  )
}
